import * as SelectPrimitive from '@radix-ui/react-select';
import { Icon } from '@/components/ui/Icon';
import { SelectChevron } from '@/components/ui/Select';
import { filterTriggerHover } from '@/components/ui/filterTriggerStyles';
import { focusSurface } from '@/components/ui/focusStyles';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

export interface MediaToolbarOption<T extends string> {
  value: T;
  label: string;
  icon?: string;
  glyph?: string;
  count?: number;
}

interface MediaToolbarSelectProps<T extends string> {
  value: T;
  options: readonly MediaToolbarOption<T>[];
  onValueChange: (value: T) => void;
  label: string;
  active?: boolean;
  className?: string;
}

const OptionVisual = ({ icon, glyph }: Pick<MediaToolbarOption<string>, 'icon' | 'glyph'>) => (
  <span aria-hidden="true" className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-gray-600">
    {icon ? <Icon name={icon} size="sm" ariaHidden /> : <span className="text-[10px] font-bold leading-none tracking-tight">{glyph}</span>}
  </span>
);

/** Compact, accessible single-choice menu shared by the Media Library filters. */
export function MediaToolbarSelect<T extends string>({
  value,
  options,
  onValueChange,
  label,
  active = false,
  className,
}: MediaToolbarSelectProps<T>) {
  const selected = options.find((option) => option.value === value);

  return (
    <SelectPrimitive.Root value={value} onValueChange={(next) => onValueChange(next as T)}>
      <SelectPrimitive.Trigger
        aria-label={`${label}: ${selected?.label ?? ''}`}
        className={cn(
          'inline-flex h-11 w-full min-w-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium',
          'transition-all duration-200 sm:h-8',
          focusSurface,
          active
            ? 'border-primary-300 bg-primary-50 text-primary-800'
            : cn('border-gray-200 bg-white text-gray-700', filterTriggerHover),
          className
        )}
      >
        <SelectPrimitive.Value className="min-w-0 flex-1">
          {selected && (
            <span className="flex min-w-0 items-center gap-1.5">
              <OptionVisual icon={selected.icon} glyph={selected.glyph} />
              <span className="truncate">{selected.label}</span>
            </span>
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon className="ml-auto shrink-0 text-gray-500">
          <SelectChevron />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          collisionPadding={12}
          className="z-popover-top max-h-[min(300px,calc(100vh-24px))] min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)] overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
        >
          <SelectPrimitive.Viewport className="max-h-[min(290px,calc(100vh-48px))] overflow-y-auto">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className="flex min-h-10 cursor-pointer select-none items-center gap-2 rounded-md px-2.5 text-sm text-gray-700 outline-none hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-primary-50 data-[state=checked]:text-primary-800 max-sm:min-h-11"
              >
                <OptionVisual icon={option.icon} glyph={option.glyph} />
                <SelectPrimitive.ItemText className="min-w-0 flex-1 truncate">{option.label}</SelectPrimitive.ItemText>
                {option.count !== undefined && (
                  <span className="text-xs tabular-nums text-gray-400" aria-hidden="true">{option.count}</span>
                )}
                <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                  <SelectPrimitive.ItemIndicator>
                    <Icon name={ICONS.CHECK} size="xs" ariaHidden />
                  </SelectPrimitive.ItemIndicator>
                </span>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
